import React, { useMemo, useState, useEffect, useRef } from 'react'
import { pathOr, propOr } from 'ramda'
import moment from 'moment'
import { isCashLesson } from 'utils/lessons'
import styled, { css } from 'styled-components'
import ExpandablePanel from 'components/ExpandablePanel'
import Checkbox from 'components/Checkbox'
import { Tooltip } from '@mui/material'
import { isNilOrEmpty } from 'utils/ramda'
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined'
import PATHS from 'utils/paths'
import { redirect } from 'utils/navigation'
import AddDebtorsPaymentModal from 'modules/debtors/components/AddDebtorsPaymentModal'
import EditStudentModal from 'modules/students/components/EditStudentModal'
import { parseNumber } from 'utils/numbers'

const DebtorsByTeacherTable = ({
  fetchDebtors,
  teacher,
  students,
  dateFrom,
  dateTo,
  getTeacherLessons,
  toggleStudentSelection,
  selectedStudents,
  onlyPrevMonthsArrears
}) => {
  const [studentsWithLessons, setStudentsWithLessons] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const hasFetched = useRef(false)
  const sectionRef = useRef(null)
  const prevStudentsRef = useRef(students)

  useEffect(() => {
    if (prevStudentsRef.current !== students) {
      prevStudentsRef.current = students
      hasFetched.current = false
      setStudentsWithLessons(null)
    }
  }, [students])

  useEffect(() => {
    if (hasFetched.current || !sectionRef.current || !getTeacherLessons || !dateFrom || !dateTo) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasFetched.current) return
        hasFetched.current = true
        setIsLoading(true)
        getTeacherLessons({
          teacherId: teacher.id,
          studentIds: students.map(s => s.id),
          dateFrom,
          dateTo
        })
          .then(lessonsByStudent => {
            const merged = students.map(s => ({
              ...s,
              lessons: lessonsByStudent.get(s.id) || []
            }))
            setStudentsWithLessons(merged)
          })
          .finally(() => setIsLoading(false))
      },
      { rootMargin: '100px', threshold: 0 }
    )

    observer.observe(sectionRef.current)
    return () => observer.disconnect()
  }, [teacher.id, students, dateFrom, dateTo, getTeacherLessons])

  const displayStudents = studentsWithLessons ?? students

  const dates = {
    currentMonth: moment().format('MMMM'),
    prevMonth: moment().subtract(1, 'month').format('MMMM'),
    prev2Month: moment().subtract(2, 'month').format('MMMM'),
  }

  const debitDetails = useMemo(() => {
    const details = displayStudents.map(student => {
      const studentLessons = propOr([], 'lessons', student)
      const lessonsByMonth = {
        afterCurrent: studentLessons.filter(lesson => moment(lesson.date).isAfter(moment().endOf('month'), 'day')),
        currentMonth: studentLessons.filter(lesson => moment(lesson.date).isBetween(moment().startOf('month'), moment().endOf('month'), 'day', '[]')),
        prevMonth: studentLessons.filter(lesson => moment(lesson.date).isBetween(moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month'), 'day', '[]')),
        prev2Month: studentLessons.filter(lesson => moment(lesson.date).isBetween(moment().subtract(2, 'month').startOf('month'), moment().subtract(2, 'month').endOf('month'), 'day', '[]'))
      }

      const lessonBalances = {
        afterCurrent: lessonsByMonth.afterCurrent.reduce((acc, lesson) => {
          if (isCashLesson(lesson.lessonType)) {
            return acc
          } else {
            return acc - (parseNumber(lesson.duration) * parseNumber(lesson.studentRate))
          }
        }, 0),
        currentMonth: lessonsByMonth.currentMonth.reduce((acc, lesson) => {
          if (isCashLesson(lesson.lessonType)) {
            return acc
          } else {
            return acc - (parseNumber(lesson.duration) * parseNumber(lesson.studentRate))
          }
        }, 0),
        prevMonth: lessonsByMonth.prevMonth.reduce((acc, lesson) => {
          if (isCashLesson(lesson.lessonType)) {
            return acc
          } else {
            return acc - (parseNumber(lesson.duration) * parseNumber(lesson.studentRate))
          }
        }, 0),
        prev2Month: lessonsByMonth.prev2Month.reduce((acc, lesson) => {
          if (isCashLesson(lesson.lessonType)) {
            return acc
          } else {
            return acc - (parseNumber(lesson.duration) * parseNumber(lesson.studentRate))
          }
        }, 0)
      }

      const currentBalance = () => {
        let balance = parseNumber(student.balance) - lessonBalances.afterCurrent
        let currentMonth = lessonBalances.currentMonth
        let prevMonth = lessonBalances.prevMonth
        let prev2Month = lessonBalances.prev2Month

        if (lessonBalances.currentMonth > balance) {
          currentMonth = lessonBalances.currentMonth
          balance -= lessonBalances.currentMonth
        } else if (lessonBalances.currentMonth === balance) {
          return {
            currentMonth: balance,
            prevMonth: 0,
            prev2Month: 0
          }
        } else {
          return {
            currentMonth: balance,
            prevMonth: 0,
            prev2Month: 0
          }
        }

        if (lessonBalances.prevMonth > balance) {
          prevMonth = lessonBalances.prevMonth
          balance -= lessonBalances.prevMonth
        } else if (lessonBalances.prevMonth === balance) {
          return {
            currentMonth,
            prevMonth: balance,
            prev2Month: 0
          }
        } else {
          return {
            currentMonth,
            prevMonth: balance,
            prev2Month: 0
          }
        }

        if (lessonBalances.prev2Month > balance) {
          prev2Month = lessonBalances.prev2Month
          balance -= lessonBalances.prev2Month
        } else if (lessonBalances.prev2Month === balance) {
          return {
            currentMonth,
            prevMonth,
            prev2Month: balance
          }
        } else {
          return {
            currentMonth,
            prevMonth,
            prev2Month: balance
          }
        }

        return {
          currentMonth,
          prevMonth,
          prev2Month
        }
      }

      return {
        ...student,
        hasLessonsInPrevMonth: lessonBalances.prevMonth < 0,
        balances: currentBalance()
      }
    })

    if (onlyPrevMonthsArrears) {
      return details.filter(s => {
        const prevMonth = parseNumber(s.balances?.prevMonth)
        const prev2Month = parseNumber(s.balances?.prev2Month)
        return prevMonth < 0 || prev2Month < 0
      })
    }
    return details
  }, [displayStudents, onlyPrevMonthsArrears])

  const handleEditCallback = () => {
    setTimeout(() => {
      fetchDebtors()
    }, 100)
  }

  if (isNilOrEmpty(debitDetails)) {
    return null
  }

  return (
    <ExpandablePanel startOpen title={teacher.name}>
      <SectionRef ref={sectionRef}>
        {isLoading && !studentsWithLessons ? (
          <LoadingState>Ładowanie danych...</LoadingState>
        ) : (
        <Table key={`${teacher.id}-table`}>
          <thead>
        <tr>
          <CheckboxCell />
          <NameCell>Uczeń</NameCell>
          <MonthCell>{dates.prev2Month}</MonthCell>
          <MonthCell>{dates.prevMonth}</MonthCell>
          <MonthCell>{dates.currentMonth}</MonthCell>
          <MonthCell>Razem</MonthCell>
          <MessageCell>Ostatnia wiadomość</MessageCell>
          <SentCell>Wysłany w tym miesiącu</SentCell>
          <ActionsCell />
        </tr>
        </thead>
        <tbody>
        {debitDetails.map(student => {
          const todayMonth = moment().format('MM')
          const todayYear = moment().format('YYYY')
          const lastMessageMonth = propOr('', 'lastMessageDate', student).slice(3,5)
          const lastMessageYear = propOr('', 'lastMessageDate', student).slice(6,10)
          const messageSentInThisMonth = (todayMonth === lastMessageMonth) && (todayYear === lastMessageYear)

          return (
            <tr
              key={student.id}
              onClick={() => redirect(`/students/${student.id}`)}
              data-id={student.id}
            >
              <CheckboxTableCell onClick={e => e.stopPropagation()}>
                {/*{!student.hasLessonsInPrevMonth && (*/}
                {/*  <Tooltip title='Brak lekcji w poprzednim miesiącu'>*/}
                {/*    <NoLessonsDot>•</NoLessonsDot>*/}
                {/*  </Tooltip>*/}
                {/*)}*/}
                <Checkbox
                  disabled={isNilOrEmpty(student.email) || isNilOrEmpty(student.phoneNumber)}
                  value={selectedStudents.includes(student.id)}
                  onChange={toggleStudentSelection(student)}
                />
              </CheckboxTableCell>
              <NameTableCell>{student.name}</NameTableCell>
              <td>{pathOr('---', ['balances', 'prev2Month'], student)}</td>
              <td>{pathOr('---', ['balances', 'prevMonth'], student)}</td>
              <td>{pathOr('---', ['balances', 'currentMonth'], student)}</td>
              <td>{student.balance}</td>
              <td>{propOr('---', 'lastMessageDate', student)}</td>
              <td>
                {messageSentInThisMonth
                  ? (
                    <Tooltip title={propOr('---', 'lastMessage', student)}>
                      <CheckIcon>✔</CheckIcon>
                    </Tooltip>
                  )
                  : (
                    <Tooltip title={propOr('---', 'lastMessage', student)}>
                      <CloseIcon>✘</CloseIcon>
                    </Tooltip>
                  )
                }
              </td>
              <Actions onClick={e => e.stopPropagation()}>
                <MailIcon
                  disabled={isNilOrEmpty(student.email) || isNilOrEmpty(student.phoneNumber)}
                  onClick={e => {
                    e.stopPropagation()
                    return isNilOrEmpty(student.email)
                      ? {}
                      : redirect(PATHS.message, [student])
                  }}
                />
                <EditStudentModal
                  student={student}
                  isSingleStudentView={false}
                  isTeacherView={false}
                  isDebtorsView={true}
                  actionCallback={handleEditCallback}
                />
                <AddDebtorsPaymentModal student={student} fetchDebtors={fetchDebtors} />
              </Actions>
            </tr>
          )
        })}
        </tbody>
      </Table>
        )}
      </SectionRef>
    </ExpandablePanel>
  )
}

export default DebtorsByTeacherTable

const SectionRef = styled.div`
  position: relative;
`

const LoadingState = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.darkGrey};
  font-size: 14px;
`

const NameTableCell = styled.td`
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
`

const Table = styled.table`
  width: 100%;
  margin-top: 20px;
  font-size: 12px;
  position: relative;
  table-layout: fixed;
  
  th, td {
    text-align: left;
    padding: 5px;
  }

  td:not(${NameTableCell}) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  tr:not(:last-of-type) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  
  th {
    border-bottom: 1px solid black;
    text-transform: uppercase;
  }
  
  tr {
    transition: all .3s;
    cursor: pointer;

    &:hover {
      background-color: ${({ theme }) => theme.colors.lightGrey};
    }
  }
`

const CheckboxCell = styled.th`
  width: 40px;
`

const NameCell = styled.th`
  width: 20%;
`

const MonthCell = styled.th`
  width: 10%;
`

const MessageCell = styled.th`
  width: 15%;
`

const SentCell = styled.th`
  width: 12%;
`

const ActionsCell = styled.th`
  width: 100px;
`

const CheckboxTableCell = styled.td`
  position: relative;
`

const NoLessonsDot = styled.span`
  position: absolute;
  left: -10px;
  font-size: 20px;
  top: 2px;
  color: red;
  cursor: help;
`

const CheckIcon = styled.div`
  color: green;
`

const CloseIcon = styled.div`
  color: red;
`

const MailIcon = styled(ForwardToInboxOutlinedIcon)`
  transition: all .3s;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.secondary.main};
  }
  
  ${({ disabled }) => disabled && css`
    color: ${({ theme }) => theme.colors.disabled};
    cursor: default;
    
    &:hover {
      color: ${({ theme }) => theme.colors.disabled};
    } 
  `}
`

const Actions = styled.div`
  text-align: right;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
`
